import React, { useState, useEffect, useMemo } from 'react';
import InvoiceHistory from './InvoiceHistory';
import { host } from '../script/variables';
import InventoryScan from './InventoryScan';
import '../style/inventory.css';

const CATEGORY_META = {
  vegetables: { label: 'Vegetables', icon: '🥦', color: '#16a34a' },
  dairy: { label: 'Dairy Products', icon: '🥛', color: '#0284c7' },
  grains_and_cereals: { label: 'Grains & Cereals', icon: '🌾', color: '#ca8a04' },
  spices_and_herbs: { label: 'Spices & Herbs', icon: '🌶️', color: '#dc2626' },
  oils_and_fats: { label: 'Oils & Fats', icon: '🫙', color: '#d97706' },
  meat_and_poultry: { label: 'Meat & Poultry', icon: '🥩', color: '#b45309' },
  seafood: { label: 'Seafood', icon: '🐟', color: '#0891b2' },
  beverages: { label: 'Beverages', icon: '☕', color: '#6d28d9' },
  fruits: { label: 'Fruits', icon: '🍋', color: '#ea580c' },
  bakery: { label: 'Bakery & Baking', icon: '🍞', color: '#92400e' },
  condiments_and_sauces: { label: 'Condiments & Sauces', icon: '🫗', color: '#7c3aed' },
  dry_goods: { label: 'Dry Goods', icon: '🥫', color: '#475569' },
  other: { label: 'Other', icon: '📦', color: '#64748b' }
};

const CATEGORIES = Object.keys(CATEGORY_META);

const Inventory = ({ cafeid }) => {
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [viewMode, setViewMode] = useState('category'); // 'category' | 'all'
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set(CATEGORIES));
  const [activeCategory, setActiveCategory] = useState('all'); // sidebar filter

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [showScan, setShowScan] = useState(false);
  const [mainView, setMainView] = useState("inventory");
  const [showHistory, setShowHistory] = useState(false);

  // Form states for add
  const [formData, setFormData] = useState({
    name: '',
    quantity: '',
    unit: 'kg',
    baseUnit: 'kg',
    baseQuantityPerUnit: '1',
    lowStockThreshold: '',
    unitPrice: '',
    category: 'other'
  });

  // Form state for update
  const [delta, setDelta] = useState('');
  const [updateReason, setUpdateReason] = useState('manual');

  const getDefaultBaseUnit = (unit) => {
    const unitMap = { kg: 'kg', g: 'g', liter: 'liter', ml: 'ml', pcs: null };
    return unitMap[unit] || null;
  };

  const getValidBaseUnits = (unit) => {
    if (unit === 'kg' || unit === 'g') return ['kg', 'g'];
    if (unit === 'liter' || unit === 'ml') return ['liter', 'ml'];
    if (unit === 'pcs') return ['kg', 'g', 'liter', 'ml','pcs'];
    return [];
  };

  const handleUnitChange = (newUnit) => {
    const defaultBaseUnit = getDefaultBaseUnit(newUnit);
    setFormData((prev) => ({
      ...prev,
      unit: newUnit,
      baseUnit: defaultBaseUnit || prev.baseUnit,
      baseQuantityPerUnit: newUnit === 'pcs' ? prev.baseQuantityPerUnit : '1'
    }));
  };


  const fetchInventory = async () => {
    if (!cafeid) { setError('Cafe ID is required'); return; }
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('partnertoken');
      const response = await fetch(`${host}/api/inventory/${cafeid}`, {
        headers: { 'Content-Type': 'application/json', Authorization: `${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInventoryItems(data.data || []);
      } else {
        const err = await response.json();
        setError(err.msg || 'Failed to fetch inventory items');
      }
    } catch {
      setError('Failed to fetch inventory items');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');

    if (!formData.name.trim()) { setError('Name is required'); return; }
    if (formData.quantity === '' || parseFloat(formData.quantity) < 0) { setError('Valid quantity is required'); return; }
    if (formData.unit === 'pcs') {
      if (!formData.baseUnit) { setError('Base unit is required for pcs'); return; }
      if (!formData.baseQuantityPerUnit || parseFloat(formData.baseQuantityPerUnit) <= 0) {
        setError('Pack size is required for pcs'); return;
      }
    }

    try {
      const token = localStorage.getItem('partnertoken');
      const payload = {
        cafeId: cafeid,
        name: formData.name.trim(),
        quantity: parseFloat(formData.quantity),
        unit: formData.unit,
        category: formData.category,
        lowStockThreshold: formData.lowStockThreshold ? parseFloat(formData.lowStockThreshold) : 0,
        unitPrice: formData.unitPrice !== '' && !isNaN(parseFloat(formData.unitPrice)) ? Math.max(0, parseFloat(formData.unitPrice)) : 0
      };
      if (formData.baseUnit) payload.baseUnit = formData.baseUnit;
      if (formData.baseQuantityPerUnit && formData.baseQuantityPerUnit !== '1') {
        payload.baseQuantityPerUnit = parseFloat(formData.baseQuantityPerUnit);
      } else if (formData.unit === 'pcs') {
        payload.baseQuantityPerUnit = parseFloat(formData.baseQuantityPerUnit || '1');
      }

      const response = await fetch(`${host}/api/inventory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `${token}` },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(data.msg?.includes('revived') ? 'Item revived successfully' : 'Item added successfully');
        setShowAddModal(false);
        setFormData({ name: '', quantity: '', unit: 'kg', baseUnit: 'kg', baseQuantityPerUnit: '1', lowStockThreshold: '', unitPrice: '', category: 'other' });
        fetchInventory();
      } else {
        const err = await response.json();
        setError(err.errors?.join(', ') || err.msg || 'Failed to add item');
      }
    } catch {
      setError('Failed to add inventory item');
    }
  };

  const handleUpdateQuantity = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!delta || isNaN(parseFloat(delta))) { setError('Valid quantity change is required'); return; }
    const deltaValue = parseFloat(delta);
    if (selectedItem.quantity + deltaValue < 0) { setError('Quantity cannot go below 0'); return; }

    try {
      const token = localStorage.getItem('partnertoken');
      const response = await fetch(`${host}/api/inventory/${selectedItem._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `${token}` },
        body: JSON.stringify({ delta: deltaValue, reason: updateReason })
      });
      if (response.ok) {
        setSuccess('Quantity updated successfully');
        setShowUpdateModal(false);
        setDelta(''); setSelectedItem(null); setUpdateReason('manual');
        fetchInventory();
      } else {
        const err = await response.json();
        setError(err.msg || 'Failed to update quantity');
      }
    } catch {
      setError('Failed to update quantity');
    }
  };

  const handleDeleteItem = async () => {
    setError(''); setSuccess('');
    try {
      const token = localStorage.getItem('partnertoken');
      const response = await fetch(`${host}/api/inventory/${selectedItem._id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `${token}` }
      });
      if (response.ok) {
        setSuccess('Item deleted successfully');
        setShowDeleteModal(false); setSelectedItem(null);
        fetchInventory();
      } else {
        const err = await response.json();
        setError(err.msg || 'Failed to delete item');
      }
    } catch {
      setError('Failed to delete item');
    }
  };

  const openUpdateModal = (item) => { setSelectedItem(item); setDelta(''); setError(''); setUpdateReason('manual'); setShowUpdateModal(true); };
  const openDeleteModal = (item) => { setSelectedItem(item); setError(''); setShowDeleteModal(true); };

  const formatPackSize = (item) => {
    if (item.unit === 'pcs' && item.baseQuantityPerUnit) return `${item.baseQuantityPerUnit}${item.baseUnit}/pc`;
    return null;
  };

  const toggleCategory = (cat) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  };
  useEffect(() => {
    if (mainView === "inventory") {
      fetchInventory();
    }
  }, [mainView]);

  // Filtered & grouped items
  const filteredItems = useMemo(() => {
    let items = inventoryItems;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter((i) => i.name.toLowerCase().includes(q));
    }
    if (activeCategory !== 'all') {
      items = items.filter((i) => (i.category || 'other') === activeCategory);
    }
    return items;
  }, [inventoryItems, searchQuery, activeCategory]);

  const grouped = useMemo(() => {
    const g = {};
    filteredItems.forEach((item) => {
      const cat = item.category || 'other';
      if (!g[cat]) g[cat] = [];
      g[cat].push(item);
    });
    return g;
  }, [filteredItems]);

  const categoriesInUse = useMemo(() => {
    const used = new Set(inventoryItems.map((i) => i.category || 'other'));
    return CATEGORIES.filter((c) => used.has(c));
  }, [inventoryItems]);

  const stats = useMemo(() => {
    const total = inventoryItems.length;
    const lowStock = inventoryItems.filter((i) => i.lowStock).length;
    const totalValue = inventoryItems.reduce((acc, i) => acc + (i.quantity * (i.unitPrice || 0)), 0);
    return { total, lowStock, totalValue };
  }, [inventoryItems]);

  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(''), 3000); return () => clearTimeout(t); }
  }, [success]);
  useEffect(() => {
    if (error) { const t = setTimeout(() => setError(''), 5000); return () => clearTimeout(t); }
  }, [error]);
  useEffect(() => { if (cafeid) fetchInventory(); }, [cafeid]);

  const resetAddForm = () => {
    setFormData({ name: '', quantity: '', unit: 'kg', baseUnit: 'kg', baseQuantityPerUnit: '1', lowStockThreshold: '', unitPrice: '', category: 'other' });
    setError('');
  };

  return (
    <div className="inv-root">
      {/* Sidebar */}
      <aside className="inv-sidebar">
        <div className="inv-sidebar-brand">
          <span className="inv-sidebar-icon">📦</span>
          <span>Inventory</span>
        </div>

        <div className="inv-stats-panel">
          <div className="inv-stat">
            <span className="inv-stat-val">{stats.total}</span>
            <span className="inv-stat-lbl">Total Items</span>
          </div>
          <div className="inv-stat inv-stat--warn">
            <span className="inv-stat-val">{stats.lowStock}</span>
            <span className="inv-stat-lbl">Low Stock</span>
          </div>
          <div className="inv-stat">
            <span className="inv-stat-val">₹{stats.totalValue.toFixed(0)}</span>
            <span className="inv-stat-lbl">Stock Value</span>
          </div>
        </div>

        <nav className="inv-nav">
          <button
            className={`inv-nav-item ${activeCategory === 'all' ? 'inv-nav-item--active' : ''}`}
            onClick={() => setActiveCategory('all')}
          >
            <span className="inv-nav-icon">🗂️</span>
            <span>All Categories</span>
            <span className="inv-nav-count">{inventoryItems.length}</span>
          </button>
          {categoriesInUse.map((cat) => {
            const meta = CATEGORY_META[cat];
            const count = inventoryItems.filter((i) => (i.category || 'other') === cat).length;
            const hasLow = inventoryItems.some((i) => (i.category || 'other') === cat && i.lowStock);
            return (
              <button
                key={cat}
                className={`inv-nav-item ${activeCategory === cat ? 'inv-nav-item--active' : ''}`}
                onClick={() => setActiveCategory(cat)}
                style={activeCategory === cat ? { '--cat-color': meta.color } : {}}
              >
                <span className="inv-nav-icon">{meta.icon}</span>
                <span>{meta.label}</span>
                <span className="inv-nav-count">{count}</span>
                {hasLow && <span className="inv-nav-dot" />}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Main */}
      <main className="inv-main">
        
        {/* Top bar */}
        <header className="inv-topbar">
          <div className="inv-topbar-left">
            <div className="inv-search-wrap">
              <span className="inv-search-icon">🔍</span>
              <input
                className="inv-search"
                placeholder="Search raw materials…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="inv-search-clear" onClick={() => setSearchQuery('')}>×</button>
              )}
            </div>
            <div className="inv-view-toggle">
              <button
                className={`inv-view-btn ${viewMode === 'category' ? 'inv-view-btn--active' : ''}`}
                onClick={() => setViewMode('category')}
                title="Category view"
              >⊞</button>
              <button
                className={`inv-view-btn ${viewMode === 'all' ? 'inv-view-btn--active' : ''}`}
                onClick={() => setViewMode('all')}
                title="Table view"
              >≡</button>
            </div>
          </div>
            {/* Invoice History */}
          <button
            className="inv-btn-add"
            onClick={() => {
              const isHistory = mainView === "invoiceHistory";

              setMainView(isHistory ? "inventory" : "invoiceHistory");
              setShowHistory(!isHistory);
            }}
          >
            {mainView === "invoiceHistory" ? "← Inventory" : "Invoice History"}
          </button>

          {/* Smart Import */}
          <button
            className="inv-btn-add"
            onClick={() =>
              setMainView(mainView === "scan" ? "inventory" : "scan")
            }
          >
            {mainView === "scan" ? "← Inventory" : "✦ Smart Import"}
          </button>
          <button
            className="inv-btn-add"
            onClick={() => { setShowAddModal(true); resetAddForm(); }}
          >
            + Add Item
          </button>
        </header>

        {/* Messages */}
        {success && <div className="inv-toast inv-toast--success">{success}</div>}
        {error && <div className="inv-toast inv-toast--error">{error}</div>}
        
        
        {mainView === "inventory" &&(
         
          <>
        {loading ? (
          <div className="inv-loading">
            <div className="inv-spinner" />
            <span>Loading inventory…</span>
          </div>
        ) : inventoryItems.length === 0 ? (
          <div className="inv-empty">
            <span className="inv-empty-icon">🛒</span>
            <h3>No items yet</h3>
            <p>Add your first raw material to get started</p>
            <button className="inv-btn-add" onClick={() => { setShowAddModal(true); resetAddForm(); }}>
              + Add Raw Material
            </button>
          </div>
        ) : viewMode === 'category' ? (
          /* CATEGORY VIEW */
          <div className="inv-category-view">
            {Object.keys(grouped).length === 0 ? (
              <div className="inv-empty">
                <span className="inv-empty-icon">🔍</span>
                <p>No items match your search</p>
              </div>
            ) : (
              Object.entries(grouped).map(([cat, items]) => {
                const meta = CATEGORY_META[cat] || CATEGORY_META.other;
                const isOpen = expandedCategories.has(cat);
                const lowCount = items.filter((i) => i.lowStock).length;
                return (
                  <section key={cat} className="inv-cat-section">
                    <button
                      className="inv-cat-header"
                      onClick={() => toggleCategory(cat)}
                      style={{ '--cat-color': meta.color }}
                    >
                      <div className="inv-cat-header-left">
                        <span className="inv-cat-emoji">{meta.icon}</span>
                        <span className="inv-cat-name">{meta.label}</span>
                        <span className="inv-cat-count">{items.length}</span>
                        {lowCount > 0 && (
                          <span className="inv-cat-low-badge">{lowCount} low</span>
                        )}
                      </div>
                      <span className={`inv-cat-chevron ${isOpen ? 'inv-cat-chevron--open' : ''}`}>›</span>
                    </button>

                    {isOpen && (
                      <div className="inv-cat-body">
                        <table className="inv-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Qty</th>
                              <th>Unit</th>
                              <th>Pack</th>
                              <th>Price/Unit</th>
                              <th>Value</th>
                              <th>Min Qty</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item) => {
                              const packSize = formatPackSize(item);
                              return (
                                <tr key={item._id} className={item.lowStock ? 'inv-row--low' : ''}>
                                  <td className="inv-td-name">{item.name}</td>
                                  <td className={`inv-td-qty ${item.lowStock ? 'inv-td-qty--low' : ''}`}>
                                    {item.quantity}
                                  </td>
                                  <td>{item.unit}</td>
                                  <td>
                                    {packSize ? (
                                      <span className="inv-pack-badge">{packSize}</span>
                                    ) : <span className="inv-dash">—</span>}
                                  </td>
                                  <td>{item.unitPrice > 0 ? `₹${Number(item.unitPrice).toFixed(2)}` : <span className="inv-dash">—</span>}</td>
                                  <td>{item.unitPrice > 0 ? `₹${(item.quantity * item.unitPrice).toFixed(0)}` : <span className="inv-dash">—</span>}</td>
                                  <td>{item.lowStockThreshold || <span className="inv-dash">—</span>}</td>
                                  <td>
                                    {item.lowStock ? (
                                      <span className="inv-status inv-status--low">⚠ Low</span>
                                    ) : (
                                      <span className="inv-status inv-status--ok">✓ OK</span>
                                    )}
                                  </td>
                                  <td>
                                    <button className="inv-action-btn inv-action-btn--update" onClick={() => openUpdateModal(item)}>
                                      Update
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </section>
                );
              })
            )}
          </div>
        ) : (
          /* TABLE VIEW */
          <div className="inv-table-view">
            <table className="inv-table inv-table--full">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Qty</th>
                  <th>Unit</th>
                  <th>Pack</th>
                  <th>Price/Unit</th>
                  <th>Value</th>
                  <th>Min Qty</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 ? (
                  <tr><td colSpan="10" className="inv-no-results">No items match your search</td></tr>
                ) : (
                  filteredItems.map((item) => {
                    const meta = CATEGORY_META[item.category || 'other'];
                    const packSize = formatPackSize(item);
                    return (
                      <tr key={item._id} className={item.lowStock ? 'inv-row--low' : ''}>
                        <td className="inv-td-name">{item.name}</td>
                        <td>
                          <span className="inv-cat-chip" style={{ '--cat-color': meta.color }}>
                            {meta.icon} {meta.label}
                          </span>
                        </td>
                        <td className={`inv-td-qty ${item.lowStock ? 'inv-td-qty--low' : ''}`}>{item.quantity}</td>
                        <td>{item.unit}</td>
                        <td>{packSize ? <span className="inv-pack-badge">{packSize}</span> : <span className="inv-dash">—</span>}</td>
                        <td>{item.unitPrice > 0 ? `₹${Number(item.unitPrice).toFixed(2)}` : <span className="inv-dash">—</span>}</td>
                        <td>{item.unitPrice > 0 ? `₹${(item.quantity * item.unitPrice).toFixed(0)}` : <span className="inv-dash">—</span>}</td>
                        <td>{item.lowStockThreshold || <span className="inv-dash">—</span>}</td>
                        <td>
                          {item.lowStock ? (
                            <span className="inv-status inv-status--low">⚠ Low</span>
                          ) : (
                            <span className="inv-status inv-status--ok">✓ OK</span>
                          )}
                        </td>
                        <td>
                          <button className="inv-action-btn inv-action-btn--update" onClick={() => openUpdateModal(item)}>Update</button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
        </>
       
        )}
        {mainView === "scan" && (
          <InventoryScan cafeid={cafeid} />
        )}

         {mainView === "invoiceHistory" && (
          <InvoiceHistory
          open={showHistory}
          onClose={() => {setShowHistory(false); setMainView("inventory")}}
          cafeId={cafeid}
        />
       
        )}
        
      </main>

      {/* ADD MODAL */}
      {showAddModal && (
        <div className="inv-overlay" onClick={() => setShowAddModal(false)}>
          <div className="inv-modal" onClick={(e) => e.stopPropagation()}>
            <div className="inv-modal-header">
              <h3>Add Raw Material</h3>
              <button className="inv-modal-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <form onSubmit={handleAddItem} className="inv-modal-form">
              {/* Category selector */}
              <div className="inv-field inv-field--full">
                <label>Category</label>
                <div className="inv-cat-grid">
                  {CATEGORIES.map((cat) => {
                    const meta = CATEGORY_META[cat];
                    return (
                      <button
                        key={cat}
                        type="button"
                        className={`inv-cat-option ${formData.category === cat ? 'inv-cat-option--active' : ''}`}
                        onClick={() => setFormData((p) => ({ ...p, category: cat }))}
                        style={formData.category === cat ? { '--cat-color': meta.color } : {}}
                      >
                        <span>{meta.icon}</span>
                        <span>{meta.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="inv-form-row">
                <div className="inv-field">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Basmati Rice"
                    required
                  />
                </div>
                <div className="inv-field">
                  <label>Quantity *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    placeholder="0"
                    required
                  />
                </div>
              </div>

              <div className="inv-field">
                <label>Unit *</label>
                <div className="inv-unit-pills">
                  {[
                    { value: 'kg', label: 'kg' },
                    { value: 'g', label: 'gram' },
                    { value: 'liter', label: 'L' },
                    { value: 'ml', label: 'ml' },
                    { value: 'pcs', label: 'pcs' }
                  ].map(({ value, label }) => (
                    <label key={value} className={`inv-pill ${formData.unit === value ? 'inv-pill--active' : ''}`}>
                      <input
                        type="radio"
                        name="unit"
                        value={value}
                        checked={formData.unit === value}
                        onChange={() => handleUnitChange(value)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              {formData.unit === 'pcs' && (
                <div className="inv-form-row">
                  <div className="inv-field">
                    <label>Base unit per piece</label>
                    <select
                      value={formData.baseUnit}
                      onChange={(e) => setFormData({ ...formData, baseUnit: e.target.value })}
                    >
                      {getValidBaseUnits(formData.unit).map((u) => (
                        <option key={u} value={u}>{u}</option>
                      ))}
                    </select>
                  </div>
                  <div className="inv-field">
                    <label>Qty per piece *</label>
                    <input
                      type="number"
                      min="0.0001"
                      step="any"
                      value={formData.baseQuantityPerUnit}
                      onChange={(e) => setFormData({ ...formData, baseQuantityPerUnit: e.target.value })}
                      placeholder="e.g. 1"
                      required
                    />
                  </div>
                </div>
              )}

              <div className="inv-form-row">
                <div className="inv-field">
                  <label>Unit Price (₹)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.unitPrice}
                    onChange={(e) => setFormData({ ...formData, unitPrice: e.target.value })}
                    placeholder="Cost per unit"
                  />
                  <small>For expense tracking</small>
                </div>
                <div className="inv-field">
                  <label>Low Stock Alert</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.lowStockThreshold}
                    onChange={(e) => setFormData({ ...formData, lowStockThreshold: e.target.value })}
                    placeholder="Alert threshold"
                  />
                  <small>Alert below this quantity</small>
                </div>
              </div>

              {error && <div className="inv-form-error">{error}</div>}

              <div className="inv-modal-footer">
                <button type="button" className="inv-btn-cancel" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className="inv-btn-submit">Add Item</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* UPDATE MODAL */}
      {showUpdateModal && selectedItem && (
        <div className="inv-overlay" onClick={() => setShowUpdateModal(false)}>
          <div className="inv-modal inv-modal--sm" onClick={(e) => e.stopPropagation()}>
            <div className="inv-modal-header">
              <h3>Update Quantity</h3>
              <button className="inv-modal-close" onClick={() => setShowUpdateModal(false)}>×</button>
            </div>
            <div className="inv-modal-form">
              <div className="inv-update-info">
                <div className="inv-update-item-name">{selectedItem.name}</div>
                <div className="inv-update-current">
                  Current: <strong>{selectedItem.quantity} {selectedItem.unit}</strong>
                </div>
                {selectedItem.unit === 'pcs' && selectedItem.baseQuantityPerUnit && (
                  <div className="inv-update-pack">Pack: {selectedItem.baseQuantityPerUnit}{selectedItem.baseUnit}/piece</div>
                )}
              </div>

              <form onSubmit={handleUpdateQuantity}>
                <div className="inv-field">
                  <label>Change Amount *</label>
                  <input
                    type="number"
                    value={delta}
                    onChange={(e) => setDelta(e.target.value)}
                    placeholder="+10 to add, -5 to reduce"
                    required
                    step="0.01"
                  />
                  <small>Positive to increase, negative to decrease</small>
                </div>

                <div className="inv-field">
                  <label>Reason</label>
                  <div className="inv-reason-pills">
                    {['purchase', 'sale', 'wastage', 'manual', 'adjustment'].map((r) => (
                      <label key={r} className={`inv-pill ${updateReason === r ? 'inv-pill--active' : ''}`}>
                        <input type="radio" name="reason" value={r} checked={updateReason === r} onChange={() => setUpdateReason(r)} />
                        {r.charAt(0).toUpperCase() + r.slice(1)}
                      </label>
                    ))}
                  </div>
                </div>

                {delta && !isNaN(parseFloat(delta)) && (
                  <div className={`inv-preview ${selectedItem.quantity + parseFloat(delta) < 0 ? 'inv-preview--error' : 'inv-preview--ok'}`}>
                    New quantity: <strong>{selectedItem.quantity + parseFloat(delta)} {selectedItem.unit}</strong>
                  </div>
                )}

                {error && <div className="inv-form-error">{error}</div>}

                <div className="inv-modal-footer">
                  <button type="button" className="inv-btn-cancel" onClick={() => setShowUpdateModal(false)}>Cancel</button>
                  <button type="submit" className="inv-btn-submit">Update</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* DELETE MODAL */}
      {showDeleteModal && selectedItem && (
        <div className="inv-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="inv-modal inv-modal--sm" onClick={(e) => e.stopPropagation()}>
            <div className="inv-modal-header">
              <h3>Delete Item</h3>
              <button className="inv-modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="inv-modal-form">
              <p style={{ marginBottom: '1rem', color: '#334155' }}>
                Are you sure you want to delete <strong>{selectedItem.name}</strong>?
              </p>
              <div className="inv-delete-details">
                <div><span>Qty:</span> {selectedItem.quantity} {selectedItem.unit}</div>
                {selectedItem.unit === 'pcs' && (
                  <div><span>Pack:</span> {selectedItem.baseQuantityPerUnit}{selectedItem.baseUnit}/pc</div>
                )}
              </div>
              <p className="inv-delete-warn">This item can be re-added to revive it.</p>
              {error && <div className="inv-form-error">{error}</div>}
              <div className="inv-modal-footer">
                <button type="button" className="inv-btn-cancel" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button type="button" className="inv-btn-delete" onClick={handleDeleteItem}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default Inventory;